import * as React from 'react';
import {
  FormProvider,
  useForm,
  type DefaultValues,
  type FieldPath,
  type Resolver,
  type UseFormReturn,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { cn } from '@/lib/utils';

/**
 * Contract: docs/FRONTEND_COMPONENT_INVENTORY.md §5.2.
 */
export interface FormProps<S extends z.ZodTypeAny> {
  schema: S;
  defaultValues: z.input<S>;
  onSubmit: (values: z.output<S>) => void | Promise<void>;
  /** PER-FIELD errors, already mapped by the feature. It is not the shape the
   *  backend sends — see the `serverErrors` note below. */
  serverErrors?: Record<string, string> | null;
  children:
    | React.ReactNode
    | ((form: UseFormReturn<z.input<S>>) => React.ReactNode);
  className?: string;
}

/**
 * The RHF + Zod bridge (§5.2, 9 screens). It mounts the `FormProvider`
 * `FormField` reads through context — the reason `FormField` does not receive
 * the error through a prop — and resolves two different kinds of validation in
 * one place: shape (Zod, client-side) and business rules (the backend, per
 * field).
 *
 * ## `z.input<S>` vs `z.output<S>`
 * They are not the same type when the schema transforms (`.transform()`,
 * `.default()`, coercions). The FORM'S VALUES — what the user types, what
 * `defaultValues` carries, what `register`/`watch`/`control` expose to the
 * children — are `z.input<S>`: the raw shape, before transforming. What
 * `onSubmit` receives is `z.output<S>`: what Zod produced AFTER parsing. A real
 * example from this very domain: an amount field the input handles as a string
 * (`z.input` carries `string`) and the schema transforms into a `number`
 * (`z.output` carries `number`) so the backend receives the type it expects.
 *
 * `@hookform/resolvers` at the installed version (^3.10, see package.json) does
 * not yet expose the third generic (`TTransformedValues`) `react-hook-form`
 * 7.43+ does support — that input/output separation only arrived in
 * `@hookform/resolvers` 4.x. At RUNTIME `zodResolver` already does the right
 * thing (it hands RHF `result.data`, the parsed/transformed values, not the raw
 * ones); what is missing is the function's type bridging to RHF's three-part
 * generic. The cast below (`asResolver`) is exactly that: a TYPE bridge confined
 * to this single line, not a general escape — the rest of the file stays
 * normally typed and the public contract (`FormProps`) gives up nothing.
 *
 * ## `serverErrors`
 * Validation has two sides. Zod covers shape and types; rules only the server
 * knows — a RUT already registered, an exhausted folio, an expired CAF — come
 * back as a 400 from the backend. Without mapping them to RHF's `setError`, the
 * user sees a generic toast and never learns which field to correct.
 *
 * **The backend's error shape is ONE, and it is not this prop.** Since B-06
 * every error response comes out as `ApiErrorDto`:
 * ```json
 * { "statusCode": 400, "error": "Bad Request",
 *   "message": "taxNumber must be a valid RUT; legalName should not be empty",
 *   "details": ["taxNumber must be a valid RUT", "legalName should not be empty"] }
 * ```
 * `message` is always a string and `details` always an array — `message` used
 * to be `string | string[]` depending on who threw the exception, and this
 * comment had to explain both shapes.
 *
 * What is still true: **`details` does not carry the field's key.** They are
 * `class-validator` sentences, one per failed rule (a field with two `@Is*`
 * failing at once contributes two), and in English — translating them is a
 * pending product decision, not an oversight. Mapping those sentences to a
 * `Record<string, string>` (extracting the property name from the start of each,
 * grouping) is the job of the FEATURE calling `Form`: it knows the endpoint and
 * knows which fields are valid. `Form` only promises what it does with the
 * already-mapped shape. It is read with `toApiError` (`services/api-error.ts`).
 *
 * Implementation care:
 * - **It is not applied twice.** The effect calling `setError` depends on
 *   `serverErrors`'s REFERENCE: if the parent re-renders with the same object
 *   (say, because of unrelated state), the effect does not run again and does
 *   not overwrite an error the user has already corrected by editing. That puts
 *   a symmetric responsibility on whoever calls `Form`: `serverErrors` has to be
 *   referentially stable across renders that carry no genuinely new error
 *   (memoized from the 422's error, not rebuilt on every render) — the same deal
 *   `DataTable` asks of `rows`.
 * - **It clears on edit.** It subscribes to `form.watch` and, when the value of
 *   a field carrying an active server error changes, clears it with
 *   `clearErrors` immediately — there is no need to wait for Zod's next
 *   validation or for a submit. A server error stuck there after the user has
 *   already corrected the field is exactly what makes a form look broken.
 *
 * ## The two `children` shapes
 * A node and a render prop produce the same `UseFormReturn`: the only difference
 * is syntactic, so the screen can choose to compose with `FormField` directly (a
 * node, the common case) or need RHF's whole object (a render prop, e.g. to
 * disable the submit with `formState.isSubmitting`).
 *
 * What it does NOT do: it renders no buttons and does not decide the fields'
 * layout.
 */
export function Form<S extends z.ZodTypeAny>({
  schema,
  defaultValues,
  onSubmit,
  serverErrors,
  children,
  className,
}: FormProps<S>): React.JSX.Element {
  type Values = z.input<S>;
  type Output = z.output<S>;

  // See the JSDoc above: a type bridge confined to this line, not a general
  // escape. `zodResolver(schema)` already does the right thing at runtime (it
  // parses and transforms); the only thing missing is the third generic this
  // version of @hookform/resolvers does not declare yet.
  const resolver = React.useMemo(
    () => zodResolver(schema) as unknown as Resolver<Values, unknown, Output>,
    [schema],
  );

  const form = useForm<Values, unknown, Output>({
    resolver,
    // `defaultValues` (the contract) is the COMPLETE object, not a partial —
    // deliberately: a form with no initial value for a required field is the
    // class of bug a `Partial` lets through silently. RHF asks for
    // `DefaultValues<Values>` (a `DeepPartial`) to allow partial defaults in
    // other uses; a complete object is a valid case of that shape, hence the
    // cast.
    defaultValues: defaultValues as DefaultValues<Values>,
  });

  const { setError, clearErrors, watch } = form;

  // Fields with an active server error not yet cleared by a user edit. It lives
  // in a ref (not in state): it must not trigger a re-render on its own, only
  // accompany `watch`'s subscription.
  const serverErrorFieldsRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    if (!serverErrors) {
      return;
    }
    for (const [field, message] of Object.entries(serverErrors)) {
      setError(field as FieldPath<Values>, { type: 'server', message });
      serverErrorFieldsRef.current.add(field);
    }
    // Intentionally depending only on `serverErrors`'s REFERENCE (see the JSDoc
    // "It is not applied twice").
  }, [serverErrors, setError]);

  React.useEffect(() => {
    const subscription = watch((_values, { name }) => {
      if (name && serverErrorFieldsRef.current.has(name)) {
        serverErrorFieldsRef.current.delete(name);
        clearErrors(name as FieldPath<Values>);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  const handleSubmit = form.handleSubmit(async (values) => {
    await onSubmit(values);
  });

  return (
    <FormProvider {...form}>
      <form
        noValidate
        className={cn(className)}
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        {typeof children === 'function'
          ? children(form as unknown as UseFormReturn<Values>)
          : children}
      </form>
    </FormProvider>
  );
}
